"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";

import {
  RunIndicatorDot,
  RunningDot,
} from "@/components/search/run-phase-pill";
import { useTalentSearch } from "@/components/search/talent-search-provider";
import { Icon } from "@/components/ui/icon";
import { useSavedSearchesList } from "@/hooks/use-search";
import { useAuth } from "@/features/auth";
import { useRecentSearches } from "@/hooks/use-recent-searches";
import { cn } from "@/lib/utils";
import {
  layoutSpring,
  MAX_VISIBLE_SEARCHES,
  searchDotColor,
  springTransition,
} from "./constants";
import {
  PendingSearchText,
  ScramblingSearchingLabel,
  SidebarSearchName,
} from "./search-labels";

type PendingPhase =
  | { phase: "scrambling" }
  | { phase: "revealing"; name: string; id: string }
  | { phase: "shining"; name: string; id: string }
  | null;

const compactNumber = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const ROW_BASE =
  "group/search relative flex h-8 w-full items-center gap-2 rounded-md px-2 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-ai/40";

const DOT_WELL = "relative flex size-4 shrink-0 items-center justify-center";

/**
 * Identity swatch. The runs store takes the slot over while the search's
 * latest run is live (spinner), failed (red) or ready but not yet opened
 * (primary); otherwise the per-search colour shows, ringed on the active row.
 */
function SearchDot({ id, active }: { id: string; active: boolean }) {
  const color = searchDotColor(id);

  return (
    <span aria-hidden data-slot="search-dot" className={DOT_WELL}>
      <RunIndicatorDot
        searchId={id}
        fallback={
          <span
            className={cn(
              "relative size-1.5 rounded-full transition-[transform,box-shadow] duration-200",
              active && "scale-125",
            )}
            style={{
              background: color,
              boxShadow: active
                ? `0 0 0 3px color-mix(in srgb, ${color} 22%, transparent)`
                : undefined,
            }}
          />
        }
      />
    </span>
  );
}

function SearchesSkeleton() {
  return (
    <div className="flex flex-col gap-px" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex h-8 items-center gap-2 px-2">
          <div className="flex size-4 items-center justify-center">
            <div className="size-1.5 rounded-full bg-contrast/10" />
          </div>
          <div
            className="h-3 rounded bg-contrast/8 shimmer shimmer-bg text-foreground"
            style={{ width: `${68 - i * 11}%` }}
          />
        </div>
      ))}
    </div>
  );
}

export function RecentSearchesList({
  expanded,
  filter = "",
}: {
  expanded: boolean;
  /** Client-side name filter typed into the section's filter box. */
  filter?: string;
}) {
  const pathname = usePathname();
  const { pendingSearchCreate, clearPendingSearch } = useTalentSearch();
  const { selectedProjectId } = useAuth();
  const { openPastSearches } = useRecentSearches();
  const { data: recentSearchesData, isLoading: searchesLoading } =
    useSavedSearchesList(selectedProjectId, {
      per_page: MAX_VISIBLE_SEARCHES,
    });
  const searches = useMemo(
    () => recentSearchesData?.searches ?? [],
    [recentSearchesData?.searches],
  );
  const totalSearches = recentSearchesData?.total ?? 0;
  const moreCount = totalSearches - searches.length;

  const knownIdsRef = useRef<Set<string>>(new Set());
  const [shiningIds, setShiningIds] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);
  const [pendingPhase, setPendingPhase] = useState<PendingPhase>(null);

  const projectId = selectedProjectId;
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      knownIdsRef.current = new Set();
      initializedRef.current = false;
      setShiningIds(new Set());
      setPendingPhase(null);
    }
  }, [projectId]);

  useEffect(() => {
    if (pendingSearchCreate && !pendingPhase) {
      setPendingPhase({ phase: "scrambling" });
    }
    if (!pendingSearchCreate && pendingPhase?.phase === "scrambling") {
      setPendingPhase(null);
    }
  }, [pendingSearchCreate, pendingPhase]);

  useEffect(() => {
    if (!searches.length) return;
    if (!initializedRef.current) {
      knownIdsRef.current = new Set(searches.map((s) => s.id));
      initializedRef.current = true;
      return;
    }
    const fresh = searches.filter((s) => !knownIdsRef.current.has(s.id));
    if (fresh.length > 0) {
      fresh.forEach((s) => knownIdsRef.current.add(s.id));
      if (pendingPhase?.phase === "scrambling") {
        const arrived = fresh[0];
        clearPendingSearch();
        setPendingPhase({
          phase: "revealing",
          name: arrived.name,
          id: arrived.id,
        });
      } else {
        const ids = new Set(shiningIds);
        fresh.forEach((s) => ids.add(s.id));
        setShiningIds(ids);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searches]);

  const handleRevealComplete = useCallback(() => {
    setPendingPhase((prev) =>
      prev && prev.phase === "revealing"
        ? { phase: "shining", name: prev.name, id: prev.id }
        : null,
    );
  }, []);

  const handlePendingShineEnd = useCallback(() => {
    setPendingPhase(null);
  }, []);

  const pendingItemId =
    pendingPhase && "id" in pendingPhase ? pendingPhase.id : null;
  const pendingScrambleOnly = pendingPhase?.phase === "scrambling";

  const trimmedFilter = filter.trim().toLowerCase();
  const filtering = trimmedFilter.length > 0;

  // While a brand-new search is being created its "Searching…" row takes the
  // top slot, so drop the last loaded row to hold the list at MAX rows; the
  // dropped one rolls into the "Show N more" count.
  const visibleSearches = useMemo(() => {
    const base = pendingScrambleOnly
      ? searches.slice(0, Math.max(0, MAX_VISIBLE_SEARCHES - 1))
      : searches;
    if (!filtering) return base;
    return base.filter((s) => s.name.toLowerCase().includes(trimmedFilter));
  }, [searches, pendingScrambleOnly, filtering, trimmedFilter]);

  const displayedMoreCount = pendingScrambleOnly
    ? moreCount + Math.max(0, searches.length - visibleSearches.length)
    : moreCount;

  const hasRecent = searches.length > 0 || Boolean(pendingPhase);

  if (!expanded) return null;

  if (searchesLoading && selectedProjectId) {
    return <SearchesSkeleton />;
  }

  if (!hasRecent) {
    return (
      <p
        data-slot="searches-empty"
        className="px-2 py-1.5 text-xs leading-4 text-contrast/40"
      >
        Your searches will show up here.
      </p>
    );
  }

  if (filtering && visibleSearches.length === 0) {
    return (
      <div className="flex flex-col gap-1 px-2 py-1.5">
        <p className="text-xs leading-4 text-contrast/40">
          No searches match &ldquo;{filter.trim()}&rdquo;
        </p>
        {totalSearches > searches.length && (
          <button
            type="button"
            onClick={openPastSearches}
            className="w-fit cursor-pointer text-xs font-medium text-contrast/60 transition-colors hover:text-heading"
          >
            Search all {totalSearches.toLocaleString()} →
          </button>
        )}
      </div>
    );
  }

  const rowTransition = { ...springTransition, layout: layoutSpring };

  return (
    <LayoutGroup id="sidebar-recent-searches">
      <div
        className="relative flex flex-col gap-px"
        role="list"
        aria-label="Recent searches"
      >
        <AnimatePresence initial={false} mode="popLayout">
          {pendingScrambleOnly && !filtering && (
            <motion.div
              key="pending-scramble"
              layout
              role="listitem"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 6 }}
              transition={rowTransition}
              className="will-change-transform"
            >
              <div className={cn(ROW_BASE, "text-heading")}>
                {/* The search has no id (or run) yet: spin unconditionally. */}
                <span aria-hidden data-slot="search-dot" className={DOT_WELL}>
                  <RunningDot />
                </span>
                <div className="flex min-w-0 flex-1 items-center">
                  <ScramblingSearchingLabel />
                </div>
              </div>
            </motion.div>
          )}

          {visibleSearches.map((search) => {
            const isPendingInline =
              Boolean(
                pendingItemId &&
                  pendingPhase &&
                  pendingPhase.phase !== "scrambling",
              ) && search.id === pendingItemId;
            const searchBase = `/talent-search/${search.id}`;
            const isActive =
              pathname === searchBase || pathname.startsWith(searchBase + "/");
            const isShining = shiningIds.has(search.id) && !isPendingInline;

            return (
              <motion.div
                key={search.id}
                layout
                role="listitem"
                initial={isShining ? { opacity: 0, scale: 0.98 } : false}
                animate={{ opacity: 1, scale: 1 }}
                transition={rowTransition}
                className="will-change-transform"
              >
                {isPendingInline && pendingPhase ? (
                  <div className={cn(ROW_BASE, "text-heading")}>
                    <SearchDot id={search.id} active={false} />
                    <div className="flex min-w-0 flex-1 items-center">
                      <PendingSearchText
                        phase={pendingPhase.phase}
                        name={
                          pendingPhase.phase === "scrambling"
                            ? null
                            : pendingPhase.name
                        }
                        onRevealComplete={handleRevealComplete}
                        onShineEnd={handlePendingShineEnd}
                      />
                    </div>
                  </div>
                ) : (
                  <Link
                    href={searchBase}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      ROW_BASE,
                      isActive
                        ? "sidebar-active text-heading"
                        : "hover:bg-contrast/4",
                    )}
                  >
                    <SearchDot id={search.id} active={isActive} />
                    <div className="flex min-w-0 flex-1 items-center">
                      {isShining ? (
                        <span
                          className="truncate bg-shine text-[13px] font-normal leading-5 text-shine animate-bg-shine-once [--gradient-color:var(--ai)]"
                          onAnimationEnd={() => {
                            setShiningIds((prev) => {
                              const next = new Set(prev);
                              next.delete(search.id);
                              return next;
                            });
                          }}
                        >
                          {search.name}
                        </span>
                      ) : (
                        <SidebarSearchName
                          name={search.name}
                          isActive={isActive}
                        />
                      )}
                    </div>
                    {search.result_count != null && (
                      <span
                        className={cn(
                          "shrink-0 text-[10.5px] font-medium tabular-nums transition-colors",
                          isActive
                            ? "text-contrast/50"
                            : "text-contrast/30 group-hover/search:text-contrast/50",
                        )}
                        title={`${search.result_count.toLocaleString()} results`}
                      >
                        {compactNumber.format(search.result_count)}
                      </span>
                    )}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Always-on entry to the full history; the count hints at what's
            not shown inline. */}
        {!filtering && (
          <motion.button
            type="button"
            layout
            transition={rowTransition}
            onClick={openPastSearches}
            className={cn(
              ROW_BASE,
              "cursor-pointer text-contrast/50 will-change-transform hover:bg-contrast/4 hover:text-heading",
            )}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              <Icon name="IconHistory" size={14} className="text-contrast/40" />
            </span>
            <span className="flex min-w-0 flex-1 items-center text-[13px] leading-5">
              Browse all searches
            </span>
            {displayedMoreCount > 0 && (
              <motion.span
                key={displayedMoreCount}
                initial={{ opacity: 0.5, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={layoutSpring}
                className="ml-auto shrink-0 text-[11px] leading-4 tabular-nums text-contrast/40"
              >
                +{displayedMoreCount}
              </motion.span>
            )}
          </motion.button>
        )}
      </div>
    </LayoutGroup>
  );
}
